import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Plus, Bell, Calendar, Trash2, Edit3, Clock } from 'lucide-react';

const RemindersSettings = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reminder_type: 'custom',
    frequency: 'once',
    target_date: '',
    notification_methods: ['email']
  });

  const reminderTypes = [
    { value: 'payment_due', label: 'Payment Due' },
    { value: 'document_expiry', label: 'Document Expiry' },
    { value: 'custom', label: 'Custom Reminder' },
    { value: 'subscription_renewal', label: 'Subscription Renewal' },
    { value: 'task_deadline', label: 'Task Deadline' }
  ];

  const frequencies = [
    { value: 'once', label: 'One Time' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const notificationMethods = [
    { value: 'email', label: 'Email' },
    { value: 'in_app', label: 'In-App Notification' },
    { value: 'sms', label: 'SMS' },
    { value: 'webhook', label: 'Webhook' }
  ];

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('organization_reminders')?.select(`
          *,
          created_by:user_profiles!organization_reminders_created_by_fkey(full_name, email)
        `)?.order('created_at', { ascending: false });

      if (error) throw error;
      setReminders(data || []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user's organization
      const { data: orgMember } = await supabase?.from('organization_members')?.select('organization_id')?.eq('user_id', user?.id)?.single();

      if (!orgMember) throw new Error('User not associated with any organization');

      const reminderData = {
        ...formData,
        organization_id: orgMember?.organization_id,
        created_by: user?.id,
        next_send_at: formData?.target_date || null
      };

      let result;
      if (editingReminder) {
        result = await supabase?.from('organization_reminders')?.update(reminderData)?.eq('id', editingReminder?.id)?.select()?.single();
      } else {
        result = await supabase?.from('organization_reminders')?.insert([reminderData])?.select()?.single();
      }

      if (result?.error) throw result?.error;

      setShowForm(false);
      setEditingReminder(null);
      resetForm();
      fetchReminders();
    } catch (error) {
      console.error('Error saving reminder:', error);
      alert(`Error saving reminder: ${error?.message}`);
    }
  };

  const handleEdit = (reminder) => {
    setEditingReminder(reminder);
    setFormData({
      title: reminder?.title || '',
      description: reminder?.description || '',
      reminder_type: reminder?.reminder_type || 'custom',
      frequency: reminder?.frequency || 'once',
      target_date: reminder?.target_date ? new Date(reminder.target_date)?.toISOString()?.slice(0, 16) : '',
      notification_methods: reminder?.notification_methods || ['email']
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this reminder?')) return;

    try {
      const { error } = await supabase?.from('organization_reminders')?.delete()?.eq('id', id);

      if (error) throw error;
      fetchReminders();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      alert(`Error deleting reminder: ${error?.message}`);
    }
  };

  const toggleReminderStatus = async (id, currentStatus) => {
    try {
      const { error } = await supabase?.from('organization_reminders')?.update({ is_active: !currentStatus })?.eq('id', id);

      if (error) throw error;
      fetchReminders();
    } catch (error) {
      console.error('Error updating reminder status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      reminder_type: 'custom',
      frequency: 'once',
      target_date: '',
      notification_methods: ['email']
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingReminder(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReminderTypeLabel = (type) => {
    const found = reminderTypes?.find(t => t?.value === type);
    return found?.label || type;
  };

  const getFrequencyLabel = (freq) => {
    const found = frequencies?.find(f => f?.value === freq);
    return found?.label || freq;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Reminders & Notifications</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Set up automated reminders for important business events and deadlines
          </p>
        </div>
        <Button 
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Reminder
        </Button>
      </div>
      {/* Form */}
      {showForm && (
        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">
              {editingReminder ? 'Edit Reminder' : 'Create New Reminder'}
            </h3>
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Title *
                </label>
                <Input
                  type="text"
                  value={formData?.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
                  placeholder="Enter reminder title"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Type
                </label>
                <Select
                  value={formData?.reminder_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, reminder_type: e?.target?.value }))}
                  options={reminderTypes}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                placeholder="Enter reminder description"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Frequency
                </label>
                <Select
                  value={formData?.frequency}
                  onChange={(e) => setFormData(prev => ({ ...prev, frequency: e?.target?.value }))}
                  options={frequencies}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Target Date & Time
                </label>
                <Input
                  type="datetime-local"
                  value={formData?.target_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_date: e?.target?.value }))}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Notification Methods
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {notificationMethods?.map((method) => (
                  <label key={method?.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData?.notification_methods?.includes(method?.value) || false}
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          setFormData(prev => ({
                            ...prev,
                            notification_methods: [...(prev?.notification_methods || []), method?.value]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            notification_methods: (prev?.notification_methods || [])?.filter(m => m !== method?.value)
                          }));
                        }
                      }}
                      className="rounded border-border text-primary"
                    />
                    <span className="text-sm text-foreground">{method?.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">
                {editingReminder ? 'Update Reminder' : 'Create Reminder'}
              </Button>
            </div>
          </form>
        </div>
      )}
      {/* Reminders List */}
      <div className="bg-surface rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-medium text-foreground">Active Reminders</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {reminders?.length || 0} reminder(s) configured
          </p>
        </div>

        <div className="divide-y divide-border">
          {reminders?.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Reminders Set</h3>
              <p className="text-muted-foreground mb-4">
                Create your first reminder to stay on top of important deadlines
              </p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Reminder
              </Button>
            </div>
          ) : (
            reminders?.map((reminder) => (
              <div key={reminder?.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-medium text-foreground">{reminder?.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        reminder?.is_active 
                          ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                      }`}>
                        {reminder?.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getReminderTypeLabel(reminder?.reminder_type)}
                      </span>
                    </div>
                    
                    {reminder?.description && (
                      <p className="text-muted-foreground mb-3">{reminder?.description}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>Frequency: {getFrequencyLabel(reminder?.frequency)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>Target: {formatDate(reminder?.target_date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Bell className="w-4 h-4" />
                        <span>Methods: {reminder?.notification_methods?.join(', ')}</span>
                      </div>
                    </div>
                    
                    {reminder?.created_by && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Created by {reminder?.created_by?.full_name || reminder?.created_by?.email}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleReminderStatus(reminder?.id, reminder?.is_active)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {reminder?.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(reminder)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(reminder?.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RemindersSettings;